import React, { useState, useRef, useEffect } from 'react';
import { ChatMode } from '../services/api';
import './ModeSelector.css';

interface ModeSelectorProps {
  currentMode: ChatMode;
  onModeChange: (mode: ChatMode) => void;
}

const ModeSelector: React.FC<ModeSelectorProps> = ({ currentMode, onModeChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState<'below' | 'above'>('below');
  const dropdownRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const modes: { value: ChatMode; label: string; description: string }[] = [
    { 
      value: 'chat', 
      label: 'Chat', 
      description: 'General accounting assistant' 
    },
    { 
      value: 'workflow', 
      label: 'Workflow', 
      description: 'Generate Python scripts' 
    },
    { 
      value: 'research', 
      label: 'Research', 
      description: 'Tax & accounting research' 
    },
  ];

  // Calculate dropdown position when opened
  useEffect(() => {
    if (isOpen && buttonRef.current) {
      const buttonRect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 150; // Approximate height for 3 items
      const spaceBelow = window.innerHeight - buttonRect.bottom;
      const spaceAbove = buttonRect.top;
      
      // If not enough space below but enough space above, position above
      if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
        setDropdownPosition('above');
      } else {
        setDropdownPosition('below');
      }
    }
  }, [isOpen]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    const handleResize = () => {
      // Recalculate position on window resize
      if (isOpen && buttonRef.current) {
        const buttonRect = buttonRef.current.getBoundingClientRect();
        const dropdownHeight = 150;
        const spaceBelow = window.innerHeight - buttonRect.bottom;
        const spaceAbove = buttonRect.top;
        
        if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
          setDropdownPosition('above');
        } else {
          setDropdownPosition('below');
        }
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('resize', handleResize);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('resize', handleResize);
    };
  }, [isOpen]);

  const handleModeSelect = (mode: ChatMode) => {
    onModeChange(mode);
    setIsOpen(false);
  };

  const currentModeInfo = modes.find(m => m.value === currentMode);

  return (
    <div className="mode-selector" ref={dropdownRef}>
      <button 
        ref={buttonRef}
        className="mode-selector-button"
        onClick={() => setIsOpen(!isOpen)}
        aria-label="Select chat mode"
      >
        <span className="mode-label">{currentModeInfo?.label}</span>
        <svg 
          className={`mode-selector-arrow ${isOpen ? 'open' : ''}`}
          width="10" 
          height="10" 
          viewBox="0 0 12 12"
        >
          <path 
            fill="currentColor" 
            d="M5.998 7.854L2.145 4 .73 5.415l5.268 5.268 5.268-5.268L9.852 4z"
          />
        </svg>
      </button>

      {isOpen && (
        <div className={`mode-selector-dropdown ${dropdownPosition}`}>
          {modes.map((mode) => (
            <button
              key={mode.value}
              className={`mode-option ${currentMode === mode.value ? 'active' : ''}`}
              onClick={() => handleModeSelect(mode.value)}
            >
              <div className="mode-option-content">
                <span className="mode-option-label">{mode.label}</span>
                <span className="mode-option-description">{mode.description}</span>
              </div>
              {currentMode === mode.value && (
                <svg width="16" height="16" viewBox="0 0 16 16" className="mode-check">
                  <path 
                    fill="currentColor" 
                    d="M13.78 4.22a.75.75 0 010 1.06l-7.25 7.25a.75.75 0 01-1.06 0L2.22 9.28a.75.75 0 111.06-1.06L6 10.94l6.72-6.72a.75.75 0 011.06 0z"
                  />
                </svg>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ModeSelector;